import { StorageService, Logger, ConfigService } from '@nextgen-web-framework/core';
import {
  RoutePaths,
  AccountProcessType,
  AccountType,
  RegFlowConfig,
  FunctionType,
} from '@commerce-profile-webcomponents/sdk';
import {
  createAndUpdateCart,
  UpdateCartRequest,
  cartApi,
  ApiProjections,
  Cart,
} from '@commerce-cart-webcomponents/sdk';
import logObject from '../../logObject';
import { appendToCart, createCheckoutWithCart } from './checkoutUtils';
import { CartUpdateType } from './enums/enums';
import { RegistrationRedirectProps } from './types/propTypes';

const logger = Logger.getInstance(logObject('registrationUtils.ts', 'registrationUtils'));

export const handleCheckoutRedirect = async (cart: Cart, cart_id_key: string) => {
  if (!cart.accountProcess?.type) {
    const localstorage = StorageService.getLocalStorage();
    const cartId = localstorage.retrieve(cart_id_key); // get this from signup --> LocalStorageKeys.CART_ID

    try {
      const res = await cartApi.getSubCart({
        cartId: cartId,
        subCartId: cartId,
        projections: ApiProjections.DETAILED,
        options: {
          skipCartResStoreInLS: true,
        },
      });
      return res.accountProcess;
    } catch (err) {
      logger.error(err, 'Error Occured');
    }
  }
  return null;
};

export const processCartFlow = async (
  dispatch: FunctionType,
  setHasError: FunctionType,
  registrationId: string,
  accountType: string,
  kitSku?: string,
  feeSku?: string,
) => {
  try {
    const updateCartPayload: UpdateCartRequest = {
      accountProcess: {
        id: registrationId,
        type: AccountProcessType.Registration,
        businessNature: accountType as AccountType,
        redirectUrl: RoutePaths.CART,
      },
      version: 0,
    };

    const cartRes = await createAndUpdateCart(updateCartPayload);
    // Append sku to cart if kitSku is present, this will handle in appendToCart
    await appendToCart(dispatch, cartRes as Cart, kitSku, feeSku, CartUpdateType.CART);
    //when we move to cart, cart does a GET cart to update cart slice.
  } catch (err) {
    logger.error(err, 'Error Occured on cart update - redirect to /cart');
    setHasError(true);
  }
};

export const processCheckoutFlow = async ({
  dispatch,
  setHasError,
  registrationId,
  accountType,
  kitSku,
  feeSku,
  useNewCartForCheckout,
  cart,
}: RegistrationRedirectProps) => {
  if (useNewCartForCheckout) {
    // This section is to use for skip cart flow, it use the starter kit to create new cart and create checkout data
    try {
      // create checkout & checkout profile is in createCheckoutWithCart
      const { NEXT_PUBLIC_REGISTER_FLOW, NEXT_PUBLIC_REGISTER_REDIRECT_URL } = ConfigService.getConfigs();

      const params = new URLSearchParams(window.location.search);
      const flowName = params.get('flowName') as RegFlowConfig;

      // Return from checkout: Default returnUri is set to sign up page, but if flowName is ShareBarFlow, it will redirect to current page
      let returnUri = `${RoutePaths.SIGN_UP}?flowName=${String(NEXT_PUBLIC_REGISTER_FLOW)}&returnUri=${String(
        NEXT_PUBLIC_REGISTER_REDIRECT_URL,
      )}`;
      if (flowName === RegFlowConfig.ShareBarFlow) {
        returnUri = window.location.href;
      }

      await createCheckoutWithCart({
        registrationId,
        processType: AccountProcessType.Registration,
        accountType: accountType,
        kitSKU: kitSku,
        feeSKU: feeSku,
        profileData: null,
        redirectUrl: returnUri, // redirect back from checkout
      });
    } catch (err) {
      logger.error(err, 'Error Occured on create new cart - redirect to /checkout');
      setHasError(true);
    }
  } else {
    // This section is same as general flow, but if kitSku is present, it will append to cart
    try {
      const updatedCart = {
        ...cart,
        accountProcess: {
          ...cart.accountProcess,
          type: AccountProcessType.Registration,
          businessNature: accountType as AccountType,
          id: registrationId,
          redirectUrl: RoutePaths.CART,
        },
      };

      const cartRes = await createAndUpdateCart(updatedCart);
      // cartSlice updated for checkout to read and create checkout.
      await appendToCart(dispatch, cartRes as Cart, kitSku, feeSku, CartUpdateType.MEMORY);
    } catch (err) {
      logger.error(err, 'Error Occured on cart update - redirect to /checkout');
      setHasError(true);
    }
  }
};

export const handleRegistrationRedirect = async ({
  dispatch,
  setHasError,
  registrationId,
  accountType,
  nextUrl,
  kitSku,
  feeSku,
  useNewCartForCheckout,
  cart,
}: RegistrationRedirectProps) => {
  try {
    switch (nextUrl) {
      case RoutePaths.CART: {
        await processCartFlow(dispatch, setHasError, registrationId, accountType, '', '');
        break;
      }
      case RoutePaths.CHECKOUT: {
        await processCheckoutFlow({
          dispatch,
          setHasError,
          registrationId,
          accountType,
          kitSku,
          feeSku,
          useNewCartForCheckout,
          cart,
        });
        break;
      }
      default:
        break;
    }
    return nextUrl;
  } catch (err) {
    logger.error(err, 'Error Occured');
    setHasError(true);
    return RoutePaths.HOME;
  }
};
